import FETCHRAW from "@/api/fetchRaw";
import { backendPublicPath } from "@/api/servicePaths";

import type {
  WorksineAppConsentRequest,
  WorksineAppInstallRequest,
  WorksineAppInstallation,
  WorksineAppInstallationResponse,
  WorksineAppInstallationStatus,
  WorksineAppRevisionRequest,
  WorksineAppRevokeRequest,
  WorksineAppsCatalogResponse,
  WorksineAppsInstallationsResponse
} from "./types";

const BASE_PATH = backendPublicPath("/apps");
const WORKSPACE_CONTEXT_401_CODES = ["workspace_missing"] as const;

const contentOf = <T>(response: { content?: T } | T): T => {
  if (response && typeof response === "object" && "content" in response) {
    return (response as { content: T }).content;
  }
  return response as T;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const installationStatuses: readonly WorksineAppInstallationStatus[] = ["pending", "approved", "suspended", "revoked"];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isInstallation = (value: unknown): value is WorksineAppInstallation => {
  if (!isRecord(value)) return false;
  if (
    typeof value.id !== "string" ||
    typeof value.app_id !== "string" ||
    typeof value.manifest_digest !== "string" ||
    typeof value.revision !== "number" ||
    !Number.isInteger(value.revision) ||
    value.revision < 1 ||
    !installationStatuses.includes(value.status as WorksineAppInstallationStatus) ||
    !Array.isArray(value.decisions)
  ) {
    return false;
  }
  return value.decisions.every((decision) => {
    if (!isRecord(decision)) return false;
    return (
      typeof decision.request_id === "string" &&
      (decision.decision === "allowed" || decision.decision === "denied") &&
      isStringArray(decision.fields) &&
      (decision.expires_at === undefined || decision.expires_at === null || typeof decision.expires_at === "string")
    );
  });
};

const assertSchemaVersion = (value: unknown): void => {
  if (!isRecord(value) || value.schema_version !== 1) {
    throw new Error("The Apps API returned an unsupported schema version.");
  }
};

const validateCatalog = (value: unknown): WorksineAppsCatalogResponse => {
  assertSchemaVersion(value);
  const catalog = value as { apps?: unknown; resources?: unknown };
  if (
    typeof (catalog as { can_manage?: unknown }).can_manage !== "boolean" ||
    !Array.isArray(catalog.apps) ||
    !Array.isArray(catalog.resources)
  ) {
    throw new Error("The Apps catalog response is invalid.");
  }
  return value as unknown as WorksineAppsCatalogResponse;
};

const validateInstallations = (value: unknown): WorksineAppsInstallationsResponse => {
  if (!isRecord(value) || !Array.isArray(value.installations)) {
    throw new Error("The installed Apps response is invalid.");
  }
  if (!value.installations.every(isInstallation)) {
    throw new Error("The installed Apps response contains an invalid installation.");
  }
  return value as unknown as WorksineAppsInstallationsResponse;
};

const validateInstallationResponse = (value: unknown): WorksineAppInstallationResponse => {
  if (!isRecord(value) || !isRecord(value.installation)) {
    throw new Error("The Apps installation response is invalid.");
  }
  if (!isInstallation(value.installation)) {
    throw new Error("The Apps installation response contains an invalid installation.");
  }
  return value as unknown as WorksineAppInstallationResponse;
};

export const getWorksineAppsCatalog = async (): Promise<WorksineAppsCatalogResponse> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/catalog/`,
    method: "GET",
    suppressAuthLogoutOn401Codes: WORKSPACE_CONTEXT_401_CODES
  });
  return validateCatalog(contentOf<WorksineAppsCatalogResponse>(response));
};

export const WORKSINE_APPS_CHANGED_EVENT = "worksine-apps-changed";

export const notifyWorksineAppsChanged = (): void => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(WORKSINE_APPS_CHANGED_EVENT));
};

export const getWorksineAppInstallations = async (): Promise<WorksineAppsInstallationsResponse> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/installations/`,
    method: "GET",
    suppressAuthLogoutOn401Codes: WORKSPACE_CONTEXT_401_CODES
  });
  return validateInstallations(contentOf<WorksineAppsInstallationsResponse>(response));
};

export const installWorksineApp = async (request: WorksineAppInstallRequest): Promise<WorksineAppInstallation> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/installations/`,
    method: "POST",
    body: JSON.stringify(request)
  });
  return validateInstallationResponse(contentOf<WorksineAppInstallationResponse>(response)).installation;
};

export const saveWorksineAppConsent = async (
  installationId: string,
  request: WorksineAppConsentRequest
): Promise<WorksineAppInstallation> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/installations/${encodeURIComponent(installationId)}/consent/`,
    method: "POST",
    body: JSON.stringify(request)
  });
  return validateInstallationResponse(contentOf<WorksineAppInstallationResponse>(response)).installation;
};

export const revokeWorksineApp = async (
  installationId: string,
  request: WorksineAppRevokeRequest
): Promise<WorksineAppInstallation> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/installations/${encodeURIComponent(installationId)}/revoke/`,
    method: "POST",
    body: JSON.stringify(request)
  });
  return validateInstallationResponse(contentOf<WorksineAppInstallationResponse>(response)).installation;
};

const changeWorksineAppStatus = async (
  installationId: string,
  action: "suspend" | "resume",
  request: WorksineAppRevisionRequest
): Promise<WorksineAppInstallation> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: `${BASE_PATH}/installations/${encodeURIComponent(installationId)}/${action}/`,
    method: "POST",
    body: JSON.stringify(request)
  });
  return validateInstallationResponse(contentOf<WorksineAppInstallationResponse>(response)).installation;
};

export const suspendWorksineApp = async (
  installationId: string,
  request: WorksineAppRevisionRequest
): Promise<WorksineAppInstallation> => changeWorksineAppStatus(installationId, "suspend", request);

export const resumeWorksineApp = async (
  installationId: string,
  request: WorksineAppRevisionRequest
): Promise<WorksineAppInstallation> => changeWorksineAppStatus(installationId, "resume", request);
